import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Star, Quote } from 'lucide-react';

const DEFAULT_TESTIMONIALS = [];

export function AnimatedTestimonials({
  title = 'WHAT PEOPLE ARE SAYING',
  subtitle = 'Real reviews from the OVR community',
  testimonials = DEFAULT_TESTIMONIALS,
  autoRotateInterval = 6000,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => { setMounted(true); }, []);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) setActiveIndex(i => (i + 1) % testimonials.length);
      else setActiveIndex(i => (i - 1 + testimonials.length) % testimonials.length);
    }
    touchStartX.current = null;
  };

  useEffect(() => {
    if (autoRotateInterval <= 0 || testimonials.length <= 1) return;
    const id = setInterval(() => {
      setActiveIndex(c => (c + 1) % testimonials.length);
    }, autoRotateInterval);
    return () => clearInterval(id);
  }, [autoRotateInterval, testimonials.length]);

  if (!testimonials.length) return null;

  return (
    <section style={{ padding: '72px 0 80px', background: '#0d0d0d' }}>
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={mounted ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: '0 24px',
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr)',
          gap: 56,
          alignItems: 'center',
        }}
        className="animated-testimonials-grid"
      >
        {/* Left: heading + dots */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(157,21,21,0.12)', border: '1px solid rgba(157,21,21,0.35)',
            borderRadius: 20, padding: '5px 14px', width: 'fit-content',
          }}>
            <Star size={13} fill="#9D1515" color="#9D1515" />
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, color: '#9D1515', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Community Voices
            </span>
          </div>

          <h2 style={{
            fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
            fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 900,
            color: '#fff', letterSpacing: '0.05em', lineHeight: 1.05, margin: 0,
          }}>
            {title}
          </h2>

          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.04em', margin: 0 }}>
            {subtitle}
          </p>

          {/* Navigation dots */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8 }}>
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                aria-label={`View testimonial ${i + 1}`}
                style={{
                  height: 10,
                  width: activeIndex === i ? 32 : 10,
                  borderRadius: 5,
                  background: activeIndex === i ? '#9D1515' : 'rgba(255,255,255,0.2)',
                  border: 'none', cursor: 'pointer', padding: 0,
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>
        </div>

        {/* Right: testimonial card stack - swipeable */}
        <div
          style={{ position: 'relative', minHeight: 340, touchAction: 'pan-y' }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <AnimatePresence mode="wait">
            {testimonials.map((t, i) => i === activeIndex && (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.45, ease: 'easeInOut' }}
                style={{ position: 'absolute', inset: 0 }}
              >
                <div style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 16, padding: '32px 28px',
                  height: '100%', display: 'flex', flexDirection: 'column', gap: 20,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                }}>
                  {/* Stars */}
                  <div style={{ display: 'flex', gap: 4 }}>
                    {Array(t.rating).fill(0).map((_, si) => (
                      <Star key={si} size={16} fill="#f5a623" color="#f5a623" />
                    ))}
                  </div>

                  {/* Quote */}
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Quote size={32} style={{ position: 'absolute', top: -6, left: -4, color: 'rgba(157,21,21,0.18)', transform: 'rotate(180deg)' }} />
                    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 15, lineHeight: 1.7, color: 'rgba(255,255,255,0.85)', margin: 0, paddingLeft: 8, position: 'relative', zIndex: 1 }}>
                      "{t.content}"
                    </p>
                  </div>

                  <div style={{ height: 1, background: 'rgba(255,255,255,0.07)' }} />

                  {/* Author */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%',
                      background: 'rgba(157,21,21,0.15)', border: '1px solid rgba(157,21,21,0.4)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, flexShrink: 0,
                    }}>
                      {t.avatar}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 700, color: '#fff', margin: 0 }}>
                        {t.handle}
                      </p>
                      {t.role && (
                        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: 0 }}>
                          {t.role}
                        </p>
                      )}
                    </div>
                    {t.source && (
                      <span style={{
                        fontFamily: 'Inter, sans-serif', fontSize: 9,
                        fontWeight: 700, letterSpacing: '0.16em',
                        textTransform: 'uppercase',
                        padding: '4px 8px',
                        border: '1px solid rgba(255,255,255,0.15)',
                        color: t.source === 'google' ? '#4285F4' : '#E1306C',
                        borderColor: t.source === 'google' ? 'rgba(66,133,244,0.4)' : 'rgba(225,48,108,0.4)',
                        background: t.source === 'google' ? 'rgba(66,133,244,0.08)' : 'rgba(225,48,108,0.08)',
                      }}>
                        {t.source === 'google' ? 'Google' : 'Instagram'}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          <div style={{ position: 'absolute', bottom: -12, left: -12, width: 60, height: 60, borderRadius: 10, background: 'rgba(157,21,21,0.05)', zIndex: 0 }} />
          <div style={{ position: 'absolute', top: -12, right: -12, width: 60, height: 60, borderRadius: 10, background: 'rgba(157,21,21,0.05)', zIndex: 0 }} />
        </div>
      </motion.div>

      <style>{`
        @media (max-width: 700px) {
          .animated-testimonials-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
      `}</style>
    </section>
  );
}

export default AnimatedTestimonials;
